import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "../auth/AuthContext";
import {
  FaGoogle,
  FaMicrosoft,
  FaLinkedinIn,
  FaTwitter,
  FaFacebookF,
} from "react-icons/fa";

// Helper: fetch with timeout wrapper
const fetchWithTimeout = (resource, options = {}, timeout = 30000) => {
  return Promise.race([
    fetch(resource, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeout)
    ),
  ]);
};

const countries = [
  {
    code: "IN",
    name: "India",
    states: [
      { name: "Karnataka", cities: ["Bangalore", "Mysore", "Mangalore"] },
      { name: "Maharashtra", cities: ["Mumbai", "Pune", "Nagpur"] },
      { name: "Delhi", cities: ["New Delhi", "Old Delhi"] },
    ],
  },
  {
    code: "US",
    name: "United States",
    states: [
      {
        name: "California",
        cities: ["Los Angeles", "San Francisco", "San Diego"],
      },
      { name: "Texas", cities: ["Houston", "Dallas", "Austin"] },
      { name: "New York", cities: ["New York City", "Buffalo", "Rochester"] },
    ],
  },
];

const socialProviders = [
  { name: "Google", icon: <FaGoogle size={24} color="#DB4437" /> },
  { name: "Microsoft", icon: <FaMicrosoft size={24} color="#F25022" /> },
  { name: "LinkedIn", icon: <FaLinkedinIn size={24} color="#0077B5" /> },
  { name: "X", icon: <FaTwitter size={24} color="#1DA1F2" /> },
  { name: "Facebook", icon: <FaFacebookF size={24} color="#4267B2" /> },
];

const SOFTWARE_CODE = "LW"; // Fixed software code for your project

const ClientRegisterPage = () => {
  // Form state
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("IN");
  const [state, setState] = useState("Karnataka");
  const [city, setCity] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Status states
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedClientCode, setGeneratedClientCode] = useState(null);
  const [loading, setLoading] = useState(false);

  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const selectedCountry = countries.find((c) => c.code === country);
  const selectedState = selectedCountry?.states.find((s) => s.name === state);

  useEffect(() => {
    if (selectedState) {
      if (!selectedState.cities.includes(city)) {
        setCity(selectedState.cities[0] || "");
      }
    } else {
      setCity("");
    }
  }, [selectedState]);

  // Automatically redirect to /login 30 seconds after success
  useEffect(() => {
    let timer;
    if (success) {
      timer = setTimeout(() => {
        try {
          navigate("/login");
        } catch (err) {
          console.error("Navigation error:", err);
        }
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const validateInputs = () => {
    if (!company.trim()) {
      setError("Company Name is required.");
      return false;
    }
    if (!email.trim() || !/.+@.+\..+/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return false;
    }
    if (!mobile.trim() || !/^\d{10,}$/.test(mobile.trim())) {
      setError("Please enter a valid mobile number with at least 10 digits.");
      return false;
    }
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return false;
    }
    if (!country) {
      setError("Please select a country.");
      return false;
    }
    if (!state) {
      setError("Please select a state.");
      return false;
    }
    if (!city) {
      setError("Please select a city.");
      return false;
    }
    if (!acceptedTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy.");
      return false;
    }
    if (!captchaToken) {
      setError("Please complete the CAPTCHA challenge.");
      return false;
    }

    setError(null);
    return true;
  };

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setCaptchaToken(null);
    }
  };

  const handleCaptchaChange = (token) => {
    try {
      setCaptchaToken(token);
      setError(null);
    } catch {
      setError("Unexpected error in CAPTCHA validation.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset error/success states and generated client code before submitting
    setError(null);
    setSuccess(null);
    setGeneratedClientCode(null);

    // Validate inputs before proceeding
    if (!validateInputs()) return;

    setLoading(true);

    try {
      // Call API with a 30-second timeout wrapper
      const response = await fetchWithTimeout(
        "/api/clients/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            softwareCode: SOFTWARE_CODE,
            name: company.trim(),
            email: email.trim(),
            phone: mobile.trim(),
            password,
            country,
            state,
            city,
            captchaToken,
          }),
        },
        30000 // 30 seconds timeout
      );

      // Safely parse JSON response
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error("Invalid server response");
      }

      // If response not OK, throw error to catch block
      if (!response.ok) {
        throw new Error(data.message || "Registration failed.");
      }

      // Success: show client code and message
      setGeneratedClientCode(data.client_code);
      setSuccess("Client registered successfully!");

      // Reset form fields
      setCompany("");
      setEmail("");
      setMobile("");
      setPassword("");
      setCountry("IN");
      setState("Karnataka");
      setCity("");
      setAcceptedTerms(false);

      // Reset reCAPTCHA widget
      resetCaptcha();
    } catch (err) {
      // Distinguish timeout error from others to give user friendly messages
      setError(
        err.message === "Request timed out"
          ? "The server took too long to respond. Please try again later."
          : err.message || "An error occurred during registration."
      );
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    alert(`Social login with ${provider} coming soon`);
  };

  const { logout } = useAuth();
  const handleLoginClick = () => {
    logout(); // Clear auth state of old user
    navigate("/login");
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 px-4">
      <div className="w-full max-w-md mt-16 p-6 bg-white rounded shadow">
        {/* Header with logo and app name */}
        <div className="flex flex-col py-6 px-2">
          <div className="flex items-center gap-2 mb-6 self-start">
            <img
              src="/logo.png"
              alt="Logo"
              className="h-10 w-10 p-[2px] rounded"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-black text-xs font-normal whitespace-nowrap">
                Lotus
              </span>
              <span className="text-black text-lg font-semibold whitespace-nowrap">
                WheelsERP
              </span>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-black text-left">
            Let's get started.
          </h2>
        </div>

        {success ? (
          <div className="text-center">
            <p className="text-green-600 mb-4" role="status">
              {success}
            </p>
            {generatedClientCode && (
              <p className="mb-6 text-blue-700 font-semibold">
                Your Client ID is:{" "}
                <span className="font-mono">{generatedClientCode}</span>
              </p>
            )}
            <button
              type="button"
              onClick={handleLoginClick}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold"
            >
              Login
            </button>

            <p className="mt-3 text-gray-500 text-sm">
              You will be redirected to login page automatically in 30 seconds.
            </p>
          </div>
        ) : (
          <>
            {error && (
              <p className="text-red-600 mb-3" role="alert">
                {error}
              </p>
            )}

            {/* Registration form */}
            <form noValidate onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Company Name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded"
                autoFocus
                required
                aria-label="Company Name"
              />

              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded"
                required
                aria-label="Email address"
              />

              <input
                type="tel"
                placeholder="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded"
                required
                aria-label="Mobile number"
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded"
                required
                aria-label="Password"
              />

              <div className="flex gap-2">
                <select
                  className="p-2 border border-gray-300 rounded w-1/3"
                  value={country}
                  onChange={(e) => {
                    const newCountryCode = e.target.value;
                    setCountry(newCountryCode);

                    const c = countries.find((c) => c.code === newCountryCode);
                    if (c && c.states.length) {
                      setState(c.states[0].name);
                      setCity(c.states[0].cities?.[0] || "");
                    } else {
                      setState("");
                      setCity("");
                    }
                  }}
                  disabled={loading}
                  required
                  aria-label="Country"
                >
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <select
                  className="p-2 border border-gray-300 rounded w-1/3"
                  value={state}
                  onChange={(e) => {
                    const newState = e.target.value;
                    setState(newState);

                    const s = selectedCountry?.states.find(
                      (s) => s.name === newState
                    );
                    setCity(s?.cities?.[0] || "");
                  }}
                  disabled={loading || !selectedCountry}
                  required
                  aria-label="State"
                >
                  {selectedCountry?.states.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <select
                  className="p-2 border border-gray-300 rounded w-1/3"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={loading || !selectedState}
                  required
                  aria-label="City"
                >
                  {selectedState?.cities.map((cityName) => (
                    <option key={cityName} value={cityName}>
                      {cityName}
                    </option>
                  ))}
                </select>
              </div>

              {/* <ReCAPTCHA
                sitekey={SITE_KEY}
                onChange={handleCaptchaChange}
                onExpired={() => {
                  setCaptchaToken(null);
                  setError("CAPTCHA expired. Please complete it again.");
                }}
                onErrored={() => {
                  setCaptchaToken(null);
                  setError("CAPTCHA error. Please try again.");
                }}
                ref={recaptchaRef}
              /> */}
              <ReCAPTCHA
                sitekey={SITE_KEY}
                onChange={handleCaptchaChange}
                onExpired={() => {
                  setCaptchaToken(null);
                  setError("CAPTCHA expired. Please complete it again.");
                }}
                onErrored={() => {
                  setCaptchaToken(null);
                  setError("CAPTCHA error occurred. Please try again.");
                }}
                ref={recaptchaRef}
              />

              <div className="flex items-center mt-2">
                <input
                  id="terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mr-2"
                  required
                  disabled={loading}
                  aria-required="true"
                />
                <label htmlFor="terms" className="text-gray-700 text-sm">
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-600"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-600"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                aria-busy={loading}
              >
                {loading ? "Creating..." : "Create my account"}
              </button>
            </form>

            <div className="mt-5 text-center">
              <p className="mb-2 text-gray-500">Sign up using</p>
              <div className="flex justify-center gap-3">
                {socialProviders.map((provider) => (
                  <button
                    key={provider.name}
                    type="button"
                    onClick={() => handleSocialLogin(provider.name)}
                    title={provider.name}
                    className="p-2 border rounded hover:bg-gray-200 flex items-center justify-center transition-colors duration-150"
                    aria-label={`Sign up with ${provider.name}`}
                  >
                    {provider.icon}
                  </button>
                ))}
              </div>
            </div>

            <p className="mt-6 text-center text-gray-700 text-sm">
              Already have an account?{" "}
              <button
                type="button"
                className="text-blue-600 underline"
                onClick={() => navigate("/login")}
              >
                Log in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientRegisterPage;
